import React, { useState, useRef, useEffect } from "react";
import { MoreVertical, CheckSquare } from "lucide-react";

const ResultsToolbar = ({
  showSavedLoads,
  resultsCount,
  platformSummaryText,
  onToggleSavedLoads,
  onRefreshSavedLoads,
  isFetchingSavedLoads,
  isInternalUser,
  onDownloadCsv,
  sortOption,
  onSortChange,
  showPartners,
  onTogglePartners,
  isSelectMode = false,
  onToggleSelectMode,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMenuOpen]);

  return (
  <div className="bg-tw-neutral-50 border-b border-gray-200">
    <div className="px-6 py-2 space-y-2">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          <span className="text-sm font-semibold text-tw-neutral-900">
            {showSavedLoads
              ? `${resultsCount} Saved Load${resultsCount === 1 ? "" : "s"}`
              : `${resultsCount} Results`}
          </span>
          {!showSavedLoads && platformSummaryText && (
            <span className="text-xs text-tw-neutral-500">
              {platformSummaryText}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Show cancel button inline when in select mode */}
          {isSelectMode && (
            <button
              type="button"
              onClick={onToggleSelectMode}
              className="px-4 py-2 text-xs font-medium rounded-lg border border-tw-info bg-tw-info/10 text-tw-info hover:bg-tw-info/20 transition-colors flex items-center gap-2"
              aria-label="Exit select mode"
            >
              <CheckSquare className="h-4 w-4" />
              <span>Cancel Selection</span>
            </button>
          )}
          <button
            type="button"
            onClick={onToggleSavedLoads}
            className="px-4 py-2 text-xs font-medium rounded-lg border border-tw-neutral-300 text-tw-neutral-700 hover:border-tw-info hover:text-tw-info hover:bg-tw-info/5 transition-colors"
          >
            {showSavedLoads ? "Show Search Results" : "View Saved Loads"}
          </button>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-xs font-medium rounded-lg border border-tw-neutral-300 text-tw-neutral-700 hover:border-tw-info hover:text-tw-info hover:bg-tw-info/5 transition-colors flex items-center justify-center"
              aria-label="More options"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-tw-neutral-200 z-10 p-3 space-y-2">
                {onToggleSelectMode && !showSavedLoads && !isSelectMode && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        onToggleSelectMode();
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2.5 text-sm text-tw-neutral-700 hover:bg-tw-neutral-50 rounded-md transition-colors flex items-center gap-3"
                    >
                      <div className="p-1.5 bg-tw-info/10 rounded-md">
                        <CheckSquare className="h-4 w-4 text-tw-info" />
                      </div>
                      <div>
                        <div className="font-medium text-tw-neutral-900">Bulk Mark as Viewed</div>
                        <div className="text-xs text-tw-neutral-500 mt-0.5">Select multiple loads at once</div>
                      </div>
                    </button>
                    <div className="border-t border-tw-neutral-100" />
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onTogglePartners();
                    setIsMenuOpen(false);
                  }}
                  className="w-full text-center px-3 py-2.5 text-sm text-tw-neutral-700 hover:bg-tw-neutral-50 rounded-md transition-colors"
                >
                  {showPartners ? "Hide Partners Column" : "Show Partners Column"}
                </button>
              </div>
            )}
          </div>
          {showSavedLoads && (
            <button
              type="button"
              onClick={onRefreshSavedLoads}
              className="px-4 py-2 text-xs font-medium rounded-lg border border-tw-neutral-300 text-tw-neutral-700 hover:border-tw-info hover:text-tw-info hover:bg-tw-info/5 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isFetchingSavedLoads}
            >
              {isFetchingSavedLoads && (
                <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              <span>Refresh Saved</span>
            </button>
          )}
          {isInternalUser && (
            <button
              type="button"
              onClick={onDownloadCsv}
              className="px-4 py-2 text-xs font-medium rounded-lg border border-tw-info text-tw-info hover:bg-tw-info/10 transition-colors"
            >
              Download CSV
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <label
              htmlFor="results-sort"
              className="text-xs font-medium text-tw-neutral-600"
            >
              {showSavedLoads ? "Sort saved loads by" : "Sort results by"}
            </label>
            <span className="text-[11px] text-tw-neutral-400">
              Recommended uses score across all sources.
            </span>
          </div>
          <div className="relative inline-flex max-w-xs">
            <select
              id="results-sort"
              value={sortOption}
              onChange={onSortChange}
              className="w-full appearance-none pl-3 pr-8 py-1.5 text-xs sm:text-sm border border-tw-neutral-300 rounded bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info hover:border-tw-info/70 transition-colors"
            >
              <option value="score">Recommended</option>
              <option value="ratePerMile">Rate Per Mile</option>
              <option value="age">Age (newest first)</option>
              <option value="revenue">Total Revenue</option>
              <option value="distance_high_to_low">Distance - High to Low</option>
              <option value="distance_low_to_high">Distance - Low to High</option>
              <option value="deadhead">Deadhead</option>
              <option value="weight">Weight</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <svg
                className="h-3.5 w-3.5 text-tw-neutral-400"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M6 8L10 12L14 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
        {/* Legacy "Find More Robinson Loads" CTA intentionally commented out */}
      </div>
    </div>
  </div>
  );
};

export default ResultsToolbar;

